/**
 * QuarantinedEmails Component
 *
 * Inbound emails held back because the sending domain's published policy failed. Nothing here has
 * consumed extraction quota — releasing an email is what hands it to processing.
 */
import { Button, Icon, Paper, SectionLabel, SimpleTable } from '@insio/ui-toolkit';
import { useFormatShortDate } from '@/hooks/use-date-formatters';
import { useQuarantinedEmails } from '../../../hooks/use-quarantined-emails';

export function QuarantinedEmails() {
  const {
    t,
    emails,
    totalCount,
    hasMore,
    loadMore,
    loading,
    error,
    handleRelease,
    handleDiscard,
    isBusy
  } = useQuarantinedEmails();
  const formatShortDate = useFormatShortDate();

  type QuarantinedEmail = (typeof emails)[number];

  // An empty quarantine is the normal state — saying so every time is noise. `loading` is not
  // part of the condition on purpose: cache-and-network starts every visit with loading=true, so
  // including it would mount the panel and unmount it again on each settings visit.
  if (!error && emails.length === 0) {
    return null;
  }

  return (
    <Paper showDivider={false}>
      <Paper.Content className='space-y-md -m-lg'>
        <SectionLabel>
          {t('quarantine.title')} ({totalCount})
        </SectionLabel>

        <div className='flex gap-sm rounded-lg bg-warn-bg px-md py-sm'>
          <Icon name='triangle-exclamation' size='sm' className='text-warn-text mt-2xs shrink-0' />
          <p className='text-sm text-warn-text'>{t('quarantine.description')}</p>
        </div>

        {error ? (
          <p className='text-sm text-error-text'>{t('quarantine.error')}</p>
        ) : (
          <SimpleTable<QuarantinedEmail>
            headers={[
              t('quarantine.fields.sender'),
              t('quarantine.fields.subject'),
              t('quarantine.fields.receivedAt'),
              t('quarantine.fields.attachments'),
              <span key='actions-header' className='flex justify-center'>
                {t('quarantine.fields.actions')}
              </span>
            ]}
            data={emails}
            scrollable
            NoContentFallback={
              <p className='text-sm text-foreground-muted'>{t('quarantine.loading')}</p>
            }
            renderRow={email => [
              <div key='sender' className='flex flex-col'>
                <span className='text-xs md:text-sm font-medium text-foreground-base'>
                  {email.sender}
                </span>
                {email.authReason && (
                  <span className='text-xs text-foreground-muted'>{email.authReason}</span>
                )}
              </div>,
              <span key='subject' className='text-xs md:text-sm text-foreground-base'>
                {email.subject ?? t('quarantine.noSubject')}
              </span>,
              <span key='received' className='text-xs md:text-sm text-foreground-base'>
                {formatShortDate(email.receivedAt)}
              </span>,
              <span key='attachments' className='text-xs md:text-sm text-foreground-base'>
                {email.attachmentCount}
              </span>,
              <div key='actions' className='flex justify-center gap-sm'>
                <Button
                  variant='primary'
                  onClick={() => void handleRelease(email.id)}
                  disabled={isBusy}
                  testId={`quarantine-release-${email.id}`}
                >
                  {t('quarantine.actions.release')}
                </Button>
                <Button
                  variant='ghost'
                  onClick={() => void handleDiscard(email.id)}
                  disabled={isBusy}
                  testId={`quarantine-discard-${email.id}`}
                >
                  {t('quarantine.actions.discard')}
                </Button>
              </div>
            ]}
          />
        )}

        {hasMore && (
          <div className='flex justify-center'>
            <Button
              variant='ghost'
              onClick={() => void loadMore()}
              disabled={isBusy || loading}
              testId='quarantine-load-more'
            >
              {t('quarantine.actions.loadMore')}
            </Button>
          </div>
        )}
      </Paper.Content>
    </Paper>
  );
}
